/*
 * Shared vocabulary for the three ITCurves app types.
 *
 * `companies.json` splits apps across three lists (`driverApps`, `backSeatApps`,
 * `bookingApps`) and tags each entry with an `appType` string. The UI needs a
 * stable lowercase key per type to drive labels, dot colors and ordering, so
 * everything that has to reason about app type goes through this module rather
 * than comparing raw `appType` strings.
 */

export const TYPE_META = {
  driver: { key: 'driver', label: 'Driver', listKey: 'driverApps' },
  backseat: { key: 'backseat', label: 'Back Seat', listKey: 'backSeatApps' },
  booking: { key: 'booking', label: 'Booking', listKey: 'bookingApps' },
};

/** Display/sort order used by the type dots and the detail modal's pills. */
export const TYPE_ORDER = ['driver', 'backseat', 'booking'];

/**
 * Normalize an `appType` value ("Driver", "BackSeat", "Back Seat") to its key.
 * Returns null for anything unrecognized so callers can skip it rather than
 * render a broken dot or pill.
 */
export function appTypeKey(appType) {
  if (typeof appType !== 'string') return null;
  const normalized = appType.toLowerCase().replace(/[^a-z]/g, '');
  return Object.prototype.hasOwnProperty.call(TYPE_META, normalized) ? normalized : null;
}

/** Flatten the three per-type lists in `comps` into one array. */
export function allApps(comps) {
  if (!comps) return [];
  return TYPE_ORDER.reduce((acc, type) => {
    const list = comps[TYPE_META[type].listKey];
    return Array.isArray(list) ? acc.concat(list) : acc;
  }, []);
}

/** The type keys one affiliate has an app for, in `TYPE_ORDER` order. */
export function typesForAffiliate(comps, iAffiliateID) {
  const owned = new Set(
    allApps(comps)
      .filter((app) => app.iAffiliateID === iAffiliateID)
      .map((app) => appTypeKey(app.appType))
  );
  return TYPE_ORDER.filter((type) => owned.has(type));
}

/**
 * One app per type the affiliate has, in `TYPE_ORDER` order.
 *
 * This drives the detail modal's type-switcher pills, which are one-per-type, so
 * a second app of a type the affiliate already has is dropped rather than
 * producing a duplicate pill. Stays in step with `typesForAffiliate` by
 * construction: mapping the result through `appTypeKey` reproduces it exactly.
 */
export function appsForAffiliate(comps, iAffiliateID) {
  const mine = allApps(comps).filter((app) => app.iAffiliateID === iAffiliateID);
  return TYPE_ORDER.map((type) => mine.find((app) => appTypeKey(app.appType) === type)).filter(
    Boolean
  );
}

/**
 * Look one app up by its `navLink` — the key the detail modal is deep-linked by
 * (`?app=<navLink>`). One real navLink contains a literal space, so callers must
 * pass the decoded value; a falsy needle finds nothing rather than matching an
 * app that has no `navLink` of its own.
 */
export function findAppByNavLink(comps, navLink) {
  if (!navLink) return null;
  return allApps(comps).find((app) => app.navLink === navLink) || null;
}

/**
 * Initials for the avatar fallback: first letter of the first two words, or the
 * first two letters of a single-word name. Digits and punctuation are ignored.
 */
export function initialsFor(name) {
  if (typeof name !== 'string') return '';
  const words = name.replace(/[^A-Za-z ]/g, '').split(' ').filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
